<script setup>
// The full join settings panel (JOIN_SETTINGS_PLAN.md §7.1) — the quick controls plus
// password, usage limit, schedule, join message and regenerate. Self-loading, so admin
// lists can open it for any entity; the entity-page card opens it as "More…".
import TlDialog from '~/components/TlDialog.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import TapDatePicker from '~/components/fields/TapDatePicker.vue';
import ShortLinkShareWrapper from '~/components/share/ShortLinkShareWrapper.vue';
import JoinSettingsControls from '~/components/join/JoinSettingsControls.vue';
import {useJoinSettings} from '~/composables/useJoinSettings';
import {useDates} from '~/composables/useDates';
import JoinPublishGate from '~/components/join/JoinPublishGate.vue';

const props = defineProps({
  referenceType: {type: Number, required: true},
  referenceId: {type: String, required: true},
  entityName: {type: String, default: ''},
  showTournamentWarning: {type: Boolean, default: false},
});
const open = defineModel({type: Boolean, default: false});
const emit = defineEmits(['changed', 'published']);

const {settings, pending, saving, errors, load, save, regenerate, downloadPdf, url, copyUrl, status} =
    useJoinSettings(() => props.referenceType, () => props.referenceId);
const enabled = computed(() => settings.value?.enabled !== false);
const {setAlertMessage} = useAlertService();
const {timeOfDay, withTimeOfDay} = useDates();

// Advanced fields are edited locally and saved with one button; the quick
// controls save as they change (same as on the card).
const form = ref({});
const passwordProtect = ref(false);
const tab = ref('settings');

const hydrate = () => {
  const link = settings.value?.link ?? {};
  form.value = {
    joinMessage: link.joinMessage ?? '',
    usageLimit: link.usageLimit ?? null,
    startsAt: link.startsAt ?? null,
    startsAtTime: link.startsAt ? timeOfDay(link.startsAt) : '00:00',
    expiresAt: link.expiresAt ?? null,
    expiresAtTime: link.expiresAt ? timeOfDay(link.expiresAt) : '23:00',
    password: '',
  };
  passwordProtect.value = !!link.isPasswordProtected;
  hydratedSnapshot.value = snapshot();
};

// Unsaved Advanced edits: closing asks first instead of silently dropping them.
const snapshot = () => JSON.stringify({...form.value, passwordProtect: passwordProtect.value});
const hydratedSnapshot = ref('');
const advancedDirty = computed(() => !!settings.value && hydratedSnapshot.value !== '' && snapshot() !== hydratedSnapshot.value);
const discardConfirm = ref(false);
const requestClose = () => {
  if (advancedDirty.value && !saving.value) {
    discardConfirm.value = true;
    return;
  }
  open.value = false;
};
const discardAndClose = () => {
  discardConfirm.value = false;
  hydrate();
  open.value = false;
};
const reviewAdvanced = () => {
  discardConfirm.value = false;
  tab.value = 'advanced';
};

watch([open, () => props.referenceId], async ([v, id], [, previousId] = []) => {
  if (!v) return;
  // Opening starts on Settings; hydrate() also runs after a save, which would
  // otherwise throw the admin off the Advanced tab the moment they saved.
  tab.value = 'settings';
  // A different entity: drop the old one's settings first, so its link is not
  // sitting there, copyable, under the new entity's name while this loads.
  if (previousId !== undefined && previousId !== id) settings.value = null;
  await load();
  hydrate();
}, {immediate: true});

const onQuick = async (patch) => {
  if (await save(patch)) {
    // The quick choices never touch the Advanced fields, so re-reading the form
    // here only threw away whatever the admin had typed there and not yet
    // saved. Switching joining on or off replaces the link, which is the one
    // case where the Advanced form really is about something else now.
    if (patch.enabled !== undefined) hydrate();
    if (patch.enabled === false) setAlertMessage('Joining switched off. The invite link no longer works');
    else if (patch.enabled === true) setAlertMessage('Joining switched on. Here is the new invite link');
    else setAlertMessage('Invite settings saved');
    emit('changed');
  }
};

// The Advanced tab carries a dot when it holds anything, so nothing set there
// is ever hidden behind a tab the admin has no reason to open.
const advancedSet = computed(() => advancedSummary.value !== '');

const advancedSummary = computed(() => {
  const link = settings.value?.link;
  if (!link) return '';
  const parts = [];
  if (link.isPasswordProtected) parts.push('password');
  if (link.usageLimit) parts.push(`limit ${link.usageLimit}`);
  if (link.startsAt || link.expiresAt) parts.push('schedule');
  if (link.joinMessage) parts.push('message');
  return parts.join(' · ');
});

const saveAdvanced = async () => {
  const f = form.value;
  const ok = await save({
    joinMessage: f.joinMessage?.trim() || null,
    usageLimit: f.usageLimit ? parseInt(f.usageLimit) || null : null,
    startsAt: f.startsAt ? withTimeOfDay(f.startsAt, f.startsAtTime || '00:00') : null,
    expiresAt: f.expiresAt ? withTimeOfDay(f.expiresAt, f.expiresAtTime || '23:00') : null,
    passwordProtect: passwordProtect.value,
    password: passwordProtect.value && f.password ? f.password : null,
    removePassword: !passwordProtect.value && !!settings.value?.link?.isPasswordProtected,
  });
  if (ok) {
    hydrate();
    setAlertMessage('Invite settings saved');
    emit('changed');
  }
};

const copyNow = async () => {
  if (await copyUrl()) setAlertMessage('Invite link copied to clipboard');
};

// ── X182 (Grant: "the admin should be made aware of this before sharing to loads of people when it's unpublished") ──
// An unpublished tournament / league cycle / americano cycle: a warning at the top with Publish in it, and Copy / Share
// ask first ("Publish and copy" / "Copy anyway" / Cancel). Copy anyway still ticks the guide's share step (copyUrl).
// Ladders and meets have no publish state (settings.published is null): nothing changes for them.
const unpublished = computed(() => settings.value?.published === false);
// X204 (R83 L1): the gate itself is components/join/JoinPublishGate (shared with the empty-pool invite card)
const tournamentApi = useCrudApi('api/v2/tournaments');
const gate = ref(null); // null | {mode: 'publish' | 'copy' | 'share', share?: Function}
const askFirst = (mode, share = null) => { gate.value = {mode, share}; };
const onCopy = () => (unpublished.value ? askFirst('copy') : copyNow());
const onShare = (click) => (unpublished.value ? askFirst('share', click) : click());
const onGateProceed = async (g) => {
  if (g?.mode === 'copy') await copyNow();
  else if (g?.mode === 'share') g.share?.();
};
const onGatePublished = async () => {
  await load();
  emit('changed');
  emit('published');
};

// X204 (R83 L2): a tournament's "Joining is open until the draw is generated." is the dialog's own to say (the header's
// Invite settings passed no prop): once published and while it has no draw, read from the tournament itself.
const tournamentDrawn = ref(null); // null: not known yet
const loadTournamentDraw = async () => {
  tournamentDrawn.value = null;
  if (props.referenceType !== 4 || !props.referenceId) return;
  try {
    await tournamentApi.getItem(props.referenceId);
    const t = tournamentApi.item.value;
    tournamentDrawn.value = t ? ((t.stages?.length ?? 0) > 0 || t.drawGenerated === true) : null;
  } catch { tournamentDrawn.value = null; }
};
watch([open, () => props.referenceId], ([v]) => { if (v) loadTournamentDraw(); }, {immediate: true});
const showJoiningOpen = computed(() => (props.referenceType === 4
    ? settings.value?.published === true && tournamentDrawn.value === false
    : props.showTournamentWarning && !!settings.value && !unpublished.value));

const onRegenerate = async () => {
  if (await regenerate()) {
    setAlertMessage('New invite link created. The old address no longer works');
    emit('changed');
  }
};

</script>

<template>
  <tl-dialog :model-value="open" :max-width="620" :persistent="advancedDirty"
             @update:model-value="(v) => v ? (open = true) : requestClose()">
    <template #title>
      <div class="jsd-title">
        <span>Invite settings</span>
        <span v-if="entityName" class="jsd-sub">{{ entityName }}</span>
      </div>
    </template>

    <!-- X202 (R82 L8): while unpublished only the warning below shows (nobody can join yet, so "joining is open" misleads) -->
    <v-alert v-if="showJoiningOpen" color="info" variant="tonal" density="compact" icon="mdi-information" class="mb-4">
      Joining is open until the draw is generated.
    </v-alert>

    <!-- X182: an unpublished competition: said before the link is shared, with Publish right there -->
    <v-alert v-if="settings && unpublished" type="warning" variant="tonal" density="compact" class="mb-4 jsd-unpub"
             data-testid="join-unpublished">
      <div class="jsd-unpub-row">
        <span><strong>Not published yet.</strong> Anyone who opens this link now can't join.</span>
        <v-btn size="small" color="primary" variant="flat" rounded="pill" class="text-none" prepend-icon="mdi-publish"
                @click="askFirst('publish')">Publish</v-btn>
      </div>
    </v-alert>

    <template v-if="pending && !settings">
      <v-skeleton-loader type="list-item-two-line@3"/>
    </template>

    <template v-else-if="settings">
      <!-- The link: one row — the address, then what you can do with it. -->
      <div v-if="enabled" class="jsd-link mb-4">
        <v-tooltip location="top" :text="url">
          <template #activator="{props: tip}">
            <button v-bind="tip" type="button" class="jsd-url" aria-label="Copy the invite link" @click="onCopy">
              <v-icon size="15" icon="mdi-link-variant"/>
              <span class="jsd-url-text">{{ url }}</span>
            </button>
          </template>
        </v-tooltip>
        <div class="jsd-link-actions">
          <tl-icon-btn tooltip="Copy link" icon="mdi-content-copy" @click="onCopy"/>
          <short-link-share-wrapper :target-path="'/invite/' + settings.link.slug">
            <template #default="{click}">
              <tl-icon-btn tooltip="Share" icon="mdi-share-variant" @click="onShare(click)"/>
            </template>
          </short-link-share-wrapper>
          <tl-icon-btn tooltip="Printable poster (PDF)" icon="mdi-file-pdf-box" @click="downloadPdf"/>
          <crud-confirm-dialog
              title="Create a new link?"
              message="The current address will stop working. Anyone you've already sent it to will need the new one."
              confirm-text="Create new link"
              cancel-text="Keep current link"
              @confirm="onRegenerate">
            <template #default="{click}">
              <tl-icon-btn tooltip="Create a new link" icon="mdi-refresh" @click="click"/>
            </template>
          </crud-confirm-dialog>
        </div>
      </div>
      <div v-if="enabled" class="jsd-linkmeta mb-4">
        {{ status?.label }} ·
        {{ settings.link.usageLimit ? `${settings.link.usedCount} of ${settings.link.usageLimit} used` : `${settings.link.usedCount} used` }}
      </div>

      <!-- The app's tab style (same as Standings/Challenges), not a form control. -->
      <div v-if="enabled" class="tl-seg jsd-tabs mb-4" role="tablist">
        <button type="button" class="tl-seg__opt" role="tab" :aria-selected="tab === 'settings'"
                :class="{'tl-seg__opt--active': tab === 'settings'}"
                @click="tab = 'settings'">
          <v-icon size="15" icon="mdi-tune-variant"/>Settings
        </button>
        <v-tooltip location="top" :text="advancedSummary" :disabled="!advancedSet">
          <template #activator="{props: tip}">
            <button v-bind="tip" type="button" class="tl-seg__opt" role="tab" :aria-selected="tab === 'advanced'"
                    :class="{'tl-seg__opt--active': tab === 'advanced'}"
                    @click="tab = 'advanced'">
              <v-icon size="15" icon="mdi-cog-outline"/>Advanced
              <span v-if="advancedSet" class="jsd-dot" role="img" :aria-label="advancedSummary"/>
            </button>
          </template>
        </v-tooltip>
      </div>

      <template v-if="tab === 'settings' || !enabled">
        <join-settings-controls :settings="settings" :saving="saving" @change="onQuick"/>
        <crud-error-display v-if="errors" class="mt-3" :errors="errors"/>
      </template>

      <!-- Advanced -->
<div v-if="enabled && tab === 'advanced'" class="jsd-adv">
          <!-- Each block says what it is for before it asks for a value: these
               are the settings an admin meets once and has to reason about. -->
          <section class="jsd-field">
            <div class="tl-label">Message on the join page</div>
            <div class="jsd-field-hint">Shown to anyone opening the link, and on the printable poster.</div>
            <v-textarea v-model="form.joinMessage" rows="2" auto-grow density="compact" variant="outlined" hide-details
                        placeholder="Bring a racket and £5.00 for the night"/>
          </section>

          <section class="jsd-field">
            <div class="tl-label">Limit how many can join</div>
            <div class="jsd-field-hint">The link stops working once this many people have used it.</div>
            <v-text-field v-model="form.usageLimit" type="number" min="1" density="compact" variant="outlined"
                          hide-details placeholder="No limit" style="max-width: 180px;"
                          :error="errors?.usageLimit != null"/>
          </section>

          <section class="jsd-field">
            <div class="tl-label">When the link works</div>
            <!-- Said here, not as placeholders: the date box keeps its placeholder for the
                 date order (dd/mm/yyyy), which these two unlabelled boxes need. -->
            <div class="jsd-field-hint">
              Leave Opens empty and the link works straight away. Leave Closes empty and it never closes. It works
              until you switch joining off.
            </div>
            <div class="jsd-when">
              <span class="jsd-when-lead">Opens</span>
              <tap-date-picker v-model="form.startsAt" min="" density="compact" hide-details clearable/>
              <v-text-field v-model="form.startsAtTime" type="time" density="compact" variant="outlined" hide-details
                            class="jsd-when-time" :disabled="!form.startsAt"/>
            </div>
            <div class="jsd-when mt-2">
              <span class="jsd-when-lead">Closes</span>
              <tap-date-picker v-model="form.expiresAt" min="" density="compact" hide-details clearable/>
              <v-text-field v-model="form.expiresAtTime" type="time" density="compact" variant="outlined" hide-details
                            class="jsd-when-time" :disabled="!form.expiresAt"/>
            </div>
          </section>

          <section class="jsd-field">
            <label class="jsd-switchrow">
              <div class="flex-fill min-w-0">
                <div class="jsd-switchrow-t">Ask for a password</div>
                <div class="jsd-field-hint mb-0">
                  {{ passwordProtect
                      ? 'People opening the link type this before they can join.'
                      : 'Anyone with the link can open it.' }}
                </div>
              </div>
              <v-switch v-model="passwordProtect" color="primary" hide-details inset density="compact"/>
            </label>
            <v-text-field v-if="passwordProtect" v-model="form.password" type="password" autocomplete="new-password"
                          class="mt-2" density="compact" variant="outlined" hide-details
                          :placeholder="settings.link.isPasswordProtected ? 'Blank keeps the current one' : 'Choose a password'"
                          :error="errors?.password != null"/>
          </section>

          <crud-error-display class="mt-1" :errors="errors"/>
        </div>
    </template>

    <crud-error-display v-if="!settings && errors" :errors="errors"/>

    <!-- X182: publish first? (an unpublished competition's Copy / Share / Publish); X204: the shared gate -->
    <join-publish-gate v-model="gate" :reference-type="referenceType" :reference-id="referenceId" :entity-name="entityName"
                       :players-per-team="settings?.playersPerTeam ?? 1" @proceed="onGateProceed" @published="onGatePublished"/>

    <!-- teleported by v-dialog; nested here to keep a single root -->
    <tl-dialog v-model="discardConfirm" title="Discard your changes?" :max-width="400">
      <p class="jsd-discard">You've changed Advanced settings that haven't been saved.</p>
      <template #actions>
        <v-btn variant="outlined" @click="reviewAdvanced">Keep editing</v-btn>
        <v-btn color="error" variant="flat" @click="discardAndClose">Discard</v-btn>
      </template>
    </tl-dialog>

    <!-- Pinned so the button never scrolls away from a long advanced form. -->
    <template v-if="settings && enabled && tab === 'advanced'" #actions>
      <v-btn variant="text" @click="requestClose">Close</v-btn>
      <v-spacer/>
      <v-btn color="primary" variant="flat" rounded="pill" :loading="saving" @click="saveAdvanced">
        Save these settings
      </v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
/* X182: the unpublished warning: words, then Publish at the end of the line (wraps under on a phone) */
.jsd-unpub-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  justify-content: space-between;
  font-size: 13.5px;
  line-height: 1.5;
}

.jsd-discard {
  font-size: 13.5px;
  line-height: 1.6;
  margin: 0;
}

.jsd-title {
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  .jsd-sub {
    font-size: 12px;
    font-weight: 500;
    color: rgb(var(--v-theme-textSubtle));
  }
}

.jsd-link {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  padding: 4px 4px 4px 10px;
}

.jsd-url {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  text-align: left;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  cursor: copy;

  :deep(.v-icon) {
    flex-shrink: 0;
    color: rgb(var(--v-theme-textSubtle));
  }
}

.jsd-url-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.jsd-link-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

/* Status and usage are reference, not the point of the panel. */
.jsd-linkmeta {
  margin-top: 6px;
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.jsd-tabs {
  width: 100%;

  .tl-seg__opt {
    flex: 1 1 0;
  }
}

/* Says "there is something in here" without opening the tab. */
.jsd-dot {
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));

  .tl-seg__opt--active & {
    background: #fff;
  }
}

.jsd-adv {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* One decision per block: what it does, then the control. */
.jsd-field-hint {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.4;
  margin: 2px 0 8px;
}

.jsd-when {
  display: flex;
  align-items: center;
  gap: 8px;

  /* The date field takes the room that is left; the time is a fixed sliver. */
  > :nth-child(2) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.jsd-when-lead {
  flex: 0 0 52px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
}

.jsd-when-time {
  flex: 0 0 116px;
}

/* On a phone the label, date and time do not fit on one line: the date field
   was squeezed until its placeholder read "dd/r". Give the label its own line
   and let the date take most of the row. */
@media (max-width: 480px) {
  .jsd-when {
    flex-wrap: wrap;
  }

  .jsd-when-lead {
    flex: 0 0 100%;
  }

  .jsd-when-time {
    flex: 0 0 104px;
  }
}

.jsd-switchrow {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;

  /* Same as the hold-players row: keep the switch its own size so it does not
     hang off the right edge on a phone. */
  :deep(.v-switch) {
    flex: 0 0 auto;
  }

  > :first-child {
    min-width: 0;
  }
}

.jsd-switchrow-t {
  font-size: 13px;
  font-weight: 700;
}

</style>
